import React, { useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import {
  CONSUMER_NOTICE,
  IMPORT_CONFIRMATION,
  STATUS_LABEL,
  STATUS_TONE,
  CONFIRMATION_STATUS,
} from "../lib/businessSearch";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Checkbox } from "./ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import {
  ArrowUpRight,
  Search,
  Building2,
  ClipboardCheck,
  Trash2,
  FileCheck2,
  Info,
  ExternalLink,
} from "lucide-react";

const inputCls =
  "rounded-none border-slate-300 focus:border-slate-900 focus:ring-1 focus:ring-slate-900";

const emptyRecord = {
  public_name: "",
  legal_name: "",
  entity_type: "",
  state_of_formation: "",
  foreign_qualification_states: "",
  entity_number: "",
  current_status: "",
  formation_date: "",
  former_names: "",
  registered_agent_name: "",
  registered_office_address: "",
  principal_address: "",
  source_jurisdiction: "",
  source_url: "",
  date_searched: "",
  date_last_verified_by_consumer: "",
  consumer_notes: "",
  confirmation_status: "uncertain",
};

/**
 * Full Business Entity Search experience.
 * - Renders the searchable 51-state grid
 * - When `caseId` is provided, also renders the case's saved entity records
 *   with actions to edit, delete, and import into the case's Parties list.
 */
export default function BusinessSearchPanel({ caseId = null, defaultPartyId = null }) {
  const [jurisdictions, setJurisdictions] = useState([]);
  const [query, setQuery] = useState("");
  const [loadingJ, setLoadingJ] = useState(true);
  const [entities, setEntities] = useState([]);
  const [loadingE, setLoadingE] = useState(false);
  const [activeJurisdiction, setActiveJurisdiction] = useState(null);
  const [form, setForm] = useState(emptyRecord);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [importTarget, setImportTarget] = useState(null); // entity being imported
  const [importAck, setImportAck] = useState(false);

  // Load jurisdictions
  useEffect(() => {
    (async () => {
      setLoadingJ(true);
      try {
        const r = await api.get("/jurisdictions");
        setJurisdictions(r.data);
      } catch (e) {
        toast.error("Could not load jurisdictions");
      } finally {
        setLoadingJ(false);
      }
    })();
  }, []);

  // Load entity records for this case
  const loadEntities = async () => {
    if (!caseId) return;
    setLoadingE(true);
    try {
      const r = await api.get(`/cases/${caseId}/entities`);
      setEntities(r.data);
    } catch (e) {
      toast.error("Could not load saved entities");
    } finally {
      setLoadingE(false);
    }
  };
  useEffect(() => {
    if (caseId) loadEntities();
  }, [caseId]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return jurisdictions;
    return jurisdictions.filter(
      (j) =>
        j.state_name.toLowerCase().includes(q) ||
        j.state_code.toLowerCase().includes(q) ||
        (j.agency || "").toLowerCase().includes(q)
    );
  }, [jurisdictions, query]);

  const openSearch = (j) => {
    // Record a "search started" moment so the form can prefill date and source
    setActiveJurisdiction(j);
    if (caseId) {
      setForm((prev) => ({
        ...prev,
        source_jurisdiction: j.state_code,
        source_url: j.search_url,
        date_searched: new Date().toISOString().slice(0, 10),
      }));
    }
    // Open the official search in a new tab.
    window.open(j.search_url, "_blank", "noopener,noreferrer");
  };

  const startNewRecord = (j) => {
    setEditingId(null);
    setForm({
      ...emptyRecord,
      source_jurisdiction: j?.state_code || "",
      source_url: j?.search_url || "",
      date_searched: new Date().toISOString().slice(0, 10),
    });
    setActiveJurisdiction(j);
  };

  const startEdit = (ent) => {
    setEditingId(ent.id);
    setForm({
      ...emptyRecord,
      ...ent,
      foreign_qualification_states: (ent.foreign_qualification_states || []).join(", "),
    });
    const j = jurisdictions.find((x) => x.state_code === ent.source_jurisdiction);
    setActiveJurisdiction(j || null);
    // Scroll to the form
    const el = document.getElementById("entity-record-form");
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const cancelForm = () => {
    setForm(emptyRecord);
    setEditingId(null);
    setActiveJurisdiction(null);
  };

  const saveRecord = async () => {
    if (!caseId) return;
    if (!form.legal_name && !form.public_name) {
      toast.error("Add at least the public or legal business name before saving");
      return;
    }
    setSaving(true);
    try {
      const body = {
        ...form,
        foreign_qualification_states: (form.foreign_qualification_states || "")
          .split(",")
          .map((s) => s.trim())
          .filter(Boolean),
      };
      if (editingId) {
        const r = await api.patch(`/cases/${caseId}/entities/${editingId}`, body);
        setEntities((prev) => prev.map((e) => (e.id === editingId ? r.data : e)));
        toast.success("Entity record updated");
      } else {
        const r = await api.post(`/cases/${caseId}/entities`, body);
        setEntities((prev) => [r.data, ...prev]);
        toast.success("Entity record saved");
      }
      cancelForm();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save entity record");
    } finally {
      setSaving(false);
    }
  };

  const deleteEntity = async (id) => {
    if (!window.confirm("Delete this saved entity record? This cannot be undone.")) return;
    try {
      await api.delete(`/cases/${caseId}/entities/${id}`);
      setEntities((prev) => prev.filter((e) => e.id !== id));
      toast.success("Entity record deleted");
    } catch (e) {
      toast.error("Could not delete");
    }
  };

  const openImport = (ent) => {
    setImportAck(false);
    setImportTarget(ent);
  };

  const confirmImport = async () => {
    if (!importTarget || !importAck) return;
    try {
      await api.post(
        `/cases/${caseId}/entities/${importTarget.id}/import-to-parties`,
        {
          confirmation_ack: true,
          target_party_id: defaultPartyId || null,
        }
      );
      toast.success("Imported to Parties. Review the party card before filing.");
      setImportTarget(null);
      setImportAck(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not import");
    }
  };

  return (
    <div className="space-y-10" data-testid="business-search-panel">
      {/* Notice */}
      <div
        className="border border-amber-300 bg-amber-50 p-4 text-sm text-amber-900 flex gap-3"
        data-testid="business-search-notice"
      >
        <Info className="w-5 h-5 mt-0.5 shrink-0 text-amber-700" />
        <div>{CONSUMER_NOTICE}</div>
      </div>

      {/* Search box */}
      <div className="border border-slate-200 p-6">
        <Label className="label-tiny mb-2 block">Find a state or agency</Label>
        <div className="relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search by state, code, or agency name"
            className={`${inputCls} pl-9`}
            data-testid="jurisdiction-search-input"
          />
        </div>
        <div className="text-xs text-slate-500 mt-2">
          {loadingJ ? "Loading jurisdictions…" : `${filtered.length} of ${jurisdictions.length} jurisdictions`}
        </div>
      </div>

      {/* Grid */}
      <div
        className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3"
        data-testid="jurisdictions-grid"
      >
        {filtered.map((j) => (
          <div
            key={j.state_code}
            className="group border border-slate-200 -mt-px -ml-px bg-white p-5 transition-shadow duration-200 hover:z-10 hover:shadow-[0_6px_18px_-12px_rgba(15,23,42,0.15)]"
            data-testid={`jurisdiction-card-${j.state_code}`}
          >
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="font-mono-tech text-[10px] tracking-widest text-slate-500 uppercase">
                  {j.state_code}
                </div>
                <div className="font-serif-display text-xl text-slate-900 leading-tight mt-1">
                  {j.state_name}
                </div>
              </div>
              <span
                className={`inline-flex items-center gap-1 border px-2 py-0.5 font-mono-tech text-[9px] uppercase tracking-wider ${STATUS_TONE[j.status] || STATUS_TONE.pending_review}`}
                title={`Status: ${STATUS_LABEL[j.status] || j.status}`}
              >
                {STATUS_LABEL[j.status] || j.status}
              </span>
            </div>
            <div className="mt-3 text-xs text-slate-600 leading-relaxed">
              <div className="font-medium text-slate-800">{j.agency}</div>
              <div className="mt-1.5 flex flex-wrap gap-1">
                {j.shows_registered_agent && (
                  <Badge variant="outline" className="rounded-none border-slate-300 text-[10px]">
                    Registered agent shown
                  </Badge>
                )}
                {j.has_document_images && (
                  <Badge variant="outline" className="rounded-none border-slate-300 text-[10px]">
                    Document images
                  </Badge>
                )}
                {j.documents_require_payment && (
                  <Badge variant="outline" className="rounded-none border-slate-300 text-[10px]">
                    Fees for copies
                  </Badge>
                )}
              </div>
              {j.instructions && (
                <div className="mt-3 text-slate-600">{j.instructions}</div>
              )}
              <div className="mt-2 text-[11px] text-slate-500">
                Last verified: <span className="tabular-nums">{j.last_verified_at || "—"}</span>
              </div>
            </div>
            <div className="mt-4 flex gap-2">
              <Button
                type="button"
                size="sm"
                onClick={() => openSearch(j)}
                data-testid={`open-search-${j.state_code}`}
                className="rounded-none bg-slate-900 hover:bg-slate-800 text-white"
              >
                Open official search <ArrowUpRight className="w-3.5 h-3.5 ml-1" />
              </Button>
              {caseId && (
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={() => startNewRecord(j)}
                  data-testid={`start-record-${j.state_code}`}
                  className="rounded-none border-slate-300"
                >
                  Record findings
                </Button>
              )}
              <a
                href={j.agency_homepage}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-slate-900 underline underline-offset-4 ml-auto self-center"
                data-testid={`agency-home-${j.state_code}`}
              >
                Agency <ExternalLink className="w-3 h-3" />
              </a>
            </div>
          </div>
        ))}
        {!loadingJ && filtered.length === 0 && (
          <div className="col-span-full border border-slate-200 p-10 text-center text-slate-500">
            No jurisdictions match your search.
          </div>
        )}
      </div>

      {/* Entity record form (only when tied to a case) */}
      {caseId && (
        <div id="entity-record-form" className="border border-slate-200 p-6 md:p-8" data-testid="entity-record-form">
          <div className="flex items-baseline justify-between mb-2">
            <div className="flex items-center gap-2">
              <Building2 className="w-5 h-5 text-slate-700" />
              <h3 className="font-serif-display text-2xl text-slate-900">
                {editingId ? "Edit entity record" : "Record what you found"}
              </h3>
            </div>
            <div className="label-tiny">
              {activeJurisdiction
                ? `Source: ${activeJurisdiction.state_name}`
                : "No source selected"}
            </div>
          </div>
          <p className="text-sm text-slate-600 mb-6 max-w-3xl">
            Enter the information exactly as it appears on the official state record. This
            record is saved to this case and can later be imported into your Parties list.
          </p>

          <div className="grid md:grid-cols-2 gap-4">
            <Field label="Public-facing business name">
              <Input value={form.public_name} onChange={(e) => setForm({ ...form, public_name: e.target.value })} className={inputCls} data-testid="er-public-name" />
            </Field>
            <Field label="Exact legal name">
              <Input value={form.legal_name} onChange={(e) => setForm({ ...form, legal_name: e.target.value })} className={inputCls} data-testid="er-legal-name" placeholder="As shown on state record" />
            </Field>
            <Field label="Entity type">
              <Input value={form.entity_type} onChange={(e) => setForm({ ...form, entity_type: e.target.value })} className={inputCls} data-testid="er-entity-type" placeholder="corporation, LLC, LP, LLP, nonprofit, etc." />
            </Field>
            <Field label="State of formation">
              <Input value={form.state_of_formation} onChange={(e) => setForm({ ...form, state_of_formation: e.target.value })} className={inputCls} data-testid="er-formation-state" placeholder="e.g., Delaware" />
            </Field>
            <Field label="Entity or file number">
              <Input value={form.entity_number} onChange={(e) => setForm({ ...form, entity_number: e.target.value })} className={inputCls} data-testid="er-entity-number" />
            </Field>
            <Field label="Current public status">
              <Input value={form.current_status} onChange={(e) => setForm({ ...form, current_status: e.target.value })} className={inputCls} data-testid="er-status" placeholder="Active, inactive, revoked, dissolved, withdrawn, etc." />
            </Field>
            <Field label="Formation date">
              <Input value={form.formation_date} onChange={(e) => setForm({ ...form, formation_date: e.target.value })} className={inputCls} data-testid="er-formation-date" placeholder="YYYY-MM-DD" />
            </Field>
            <Field label="Foreign qualification states">
              <Input value={form.foreign_qualification_states} onChange={(e) => setForm({ ...form, foreign_qualification_states: e.target.value })} className={inputCls} data-testid="er-foreign-states" placeholder="Comma-separated codes (e.g., CA, NY, TX)" />
            </Field>
            <Field label="Registered-agent name">
              <Input value={form.registered_agent_name} onChange={(e) => setForm({ ...form, registered_agent_name: e.target.value })} className={inputCls} data-testid="er-agent-name" />
            </Field>
            <Field label="Registered-office address">
              <Input value={form.registered_office_address} onChange={(e) => setForm({ ...form, registered_office_address: e.target.value })} className={inputCls} data-testid="er-agent-address" />
            </Field>
            <Field label="Principal address">
              <Input value={form.principal_address} onChange={(e) => setForm({ ...form, principal_address: e.target.value })} className={inputCls} data-testid="er-principal-address" />
            </Field>
            <Field label="Former business names">
              <Input value={form.former_names} onChange={(e) => setForm({ ...form, former_names: e.target.value })} className={inputCls} data-testid="er-former-names" placeholder="Optional — comma-separated" />
            </Field>
            <Field label="Source URL">
              <Input value={form.source_url} onChange={(e) => setForm({ ...form, source_url: e.target.value })} className={inputCls} data-testid="er-source-url" />
            </Field>
            <Field label="Date searched">
              <Input type="date" value={form.date_searched} onChange={(e) => setForm({ ...form, date_searched: e.target.value })} className={inputCls} data-testid="er-date-searched" />
            </Field>
            <Field label="Date last verified by you">
              <Input type="date" value={form.date_last_verified_by_consumer} onChange={(e) => setForm({ ...form, date_last_verified_by_consumer: e.target.value })} className={inputCls} data-testid="er-date-verified" />
            </Field>
            <Field label="Confirmation status">
              <Select
                value={form.confirmation_status}
                onValueChange={(v) => setForm({ ...form, confirmation_status: v })}
              >
                <SelectTrigger className={inputCls} data-testid="er-confirmation-status">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {CONFIRMATION_STATUS.map((s) => (
                    <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
          </div>
          <div className="mt-4">
            <Field label="Notes">
              <Textarea
                value={form.consumer_notes}
                onChange={(e) => setForm({ ...form, consumer_notes: e.target.value })}
                className={`${inputCls} min-h-[90px]`}
                data-testid="er-notes"
                placeholder="Anything unclear, contradictory, or worth double-checking later."
              />
            </Field>
          </div>

          <div className="flex justify-end gap-2 mt-6 pt-4 border-t border-slate-200">
            {editingId && (
              <Button
                type="button"
                variant="outline"
                onClick={cancelForm}
                className="rounded-none border-slate-300"
                data-testid="er-cancel-btn"
              >
                Cancel
              </Button>
            )}
            <Button
              type="button"
              onClick={saveRecord}
              disabled={saving}
              className="rounded-none bg-slate-900 hover:bg-slate-800 text-white"
              data-testid="er-save-btn"
            >
              <FileCheck2 className="w-4 h-4 mr-1.5" />
              {saving ? "Saving…" : editingId ? "Update record" : "Save record"}
            </Button>
          </div>
        </div>
      )}

      {/* Saved entities list */}
      {caseId && (
        <div>
          <div className="flex items-baseline justify-between mb-4">
            <h3 className="font-serif-display text-2xl text-slate-900">Saved entity records</h3>
            <span className="label-tiny">{entities.length} record{entities.length === 1 ? "" : "s"}</span>
          </div>
          {loadingE ? (
            <div className="border border-slate-200 p-6 label-tiny">Loading…</div>
          ) : entities.length === 0 ? (
            <div className="border border-dashed border-slate-300 p-8 text-center text-slate-500 text-sm" data-testid="entities-empty">
              No entity records saved yet. Use the state grid above to open an official
              search, then record what you find here.
            </div>
          ) : (
            <div className="border border-slate-200" data-testid="entities-list">
              {entities.map((ent) => (
                <div key={ent.id} className="border-t border-slate-200 first:border-t-0 p-5 flex flex-col md:flex-row gap-4" data-testid={`entity-row-${ent.id}`}>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <div className="font-serif-display text-lg text-slate-900 truncate">
                        {ent.legal_name || ent.public_name || "(unnamed)"}
                      </div>
                      <ConfirmationPill status={ent.confirmation_status} />
                    </div>
                    <div className="text-xs text-slate-600 mt-1">
                      {[ent.entity_type, ent.state_of_formation && `formed in ${ent.state_of_formation}`, ent.entity_number]
                        .filter(Boolean)
                        .join(" · ")}
                    </div>
                    {ent.registered_agent_name && (
                      <div className="text-xs text-slate-600 mt-1">
                        Registered agent: <span className="font-medium">{ent.registered_agent_name}</span>
                        {ent.registered_office_address ? ` at ${ent.registered_office_address}` : ""}
                      </div>
                    )}
                    {ent.principal_address && (
                      <div className="text-xs text-slate-600 mt-1">Principal address: {ent.principal_address}</div>
                    )}
                    <div className="text-[11px] text-slate-500 mt-2">
                      {ent.source_jurisdiction ? `Source: ${ent.source_jurisdiction}` : ""}
                      {ent.date_searched ? ` · Searched ${ent.date_searched}` : ""}
                      {ent.source_url ? (
                        <>
                          {" · "}
                          <a href={ent.source_url} target="_blank" rel="noopener noreferrer" className="underline">
                            source
                          </a>
                        </>
                      ) : null}
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-2 md:flex-col md:items-end">
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      className="rounded-none border-slate-300"
                      onClick={() => startEdit(ent)}
                      data-testid={`edit-entity-${ent.id}`}
                    >
                      Edit
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      className="rounded-none bg-slate-900 hover:bg-slate-800 text-white"
                      onClick={() => openImport(ent)}
                      data-testid={`import-entity-${ent.id}`}
                    >
                      <ClipboardCheck className="w-4 h-4 mr-1.5" /> Import to Parties
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      className="rounded-none text-red-700 hover:bg-red-50"
                      onClick={() => deleteEntity(ent.id)}
                      data-testid={`delete-entity-${ent.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Import confirmation dialog */}
      <Dialog open={!!importTarget} onOpenChange={(open) => { if (!open) { setImportTarget(null); setImportAck(false); } }}>
        <DialogContent className="max-w-lg rounded-none" data-testid="import-confirmation-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif-display text-2xl">Confirm before importing</DialogTitle>
            <DialogDescription className="text-slate-700 text-sm mt-2">
              You are about to add this business to the Parties section of your complaint
              draft. ProSe.Claims cannot determine which party is legally responsible or
              where legal documents must be served.
            </DialogDescription>
          </DialogHeader>
          {importTarget && (
            <div className="border border-slate-200 p-3 text-sm">
              <div className="font-medium text-slate-900">
                {importTarget.legal_name || importTarget.public_name || "(unnamed)"}
              </div>
              {importTarget.entity_type && (
                <div className="text-xs text-slate-600">{importTarget.entity_type}{importTarget.state_of_formation ? ` · ${importTarget.state_of_formation}` : ""}</div>
              )}
              {importTarget.registered_agent_name && (
                <div className="text-xs text-slate-600 mt-1">Registered agent: {importTarget.registered_agent_name}</div>
              )}
            </div>
          )}
          <div className="flex items-start gap-3 border border-amber-300 bg-amber-50 p-4 mt-2">
            <Checkbox
              id="import-ack-checkbox"
              checked={importAck}
              onCheckedChange={(v) => setImportAck(!!v)}
              className="mt-0.5"
              data-testid="import-ack-checkbox"
            />
            <label htmlFor="import-ack-checkbox" className="text-sm text-amber-900 leading-relaxed cursor-pointer">
              {IMPORT_CONFIRMATION}
            </label>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" className="rounded-none border-slate-300" onClick={() => { setImportTarget(null); setImportAck(false); }} data-testid="import-cancel-btn">
              Cancel
            </Button>
            <Button
              className="rounded-none bg-slate-900 hover:bg-slate-800 text-white"
              disabled={!importAck}
              onClick={confirmImport}
              data-testid="import-confirm-btn"
            >
              Import to Parties
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div className="space-y-2">
      <Label className="label-tiny">{label}</Label>
      {children}
    </div>
  );
}

function ConfirmationPill({ status }) {
  const tone =
    status === "confirmed"
      ? "text-emerald-800 bg-emerald-50 border-emerald-200"
      : status === "disputed"
      ? "text-red-800 bg-red-50 border-red-200"
      : "text-slate-700 bg-slate-100 border-slate-200";
  const label = status === "confirmed" ? "Confirmed" : status === "disputed" ? "Needs review" : "Uncertain";
  return (
    <span
      className={`border px-2 py-0.5 font-mono-tech text-[10px] uppercase tracking-wider ${tone}`}
    >
      {label}
    </span>
  );
}
